// Single source-of-truth for the content selection shared between the Content Library and Link
// Campaigns pages. It lives in `sessionStorage` (client-owned, tab-scoped): it survives a refresh, is
// dropped when the tab/browser closes, and is not attached to page requests as a cookie — it is only
// read on the client and passed explicitly (e.g. into the load server action). Mirrors the Content
// Moderation flow's `sessionStorage` handoff.
export const CONTENT_LINK_SELECTION_STORAGE_KEY = 'contentLinkSelection';

export const serializeLinkCampaignsSelection = (selectedIds: string[]): string => selectedIds.join(',');

export const parseLinkCampaignsSelection = (rawValue?: string | null): string[] => {
  if (!rawValue) return [];

  const decodedValue = (() => {
    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  })();

  if (decodedValue.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(decodedValue);
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
    } catch {
      return [];
    }
  }

  return decodedValue
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
};

export const readLinkCampaignsSelection = (): string[] => {
  if (typeof window === 'undefined') return [];

  return parseLinkCampaignsSelection(window.sessionStorage.getItem(CONTENT_LINK_SELECTION_STORAGE_KEY));
};

export const writeLinkCampaignsSelection = (selectedIds: string[]): void => {
  if (typeof window === 'undefined') return;

  window.sessionStorage.setItem(CONTENT_LINK_SELECTION_STORAGE_KEY, serializeLinkCampaignsSelection(selectedIds));
};

export const clearLinkCampaignsSelection = (): void => {
  if (typeof window === 'undefined') return;

  window.sessionStorage.removeItem(CONTENT_LINK_SELECTION_STORAGE_KEY);
};
